import { useEffect, useId, useRef, useState } from 'react';
import './Modal.css';

function loadMercadoPagoSdk() {
  return new Promise((resolve, reject) => {
    if (window.MercadoPago) {
      resolve(window.MercadoPago);
      return;
    }

    const existing = document.getElementById('mp-sdk-v2');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.MercadoPago), { once: true });
      existing.addEventListener('error', () => reject(new Error('No se pudo cargar el SDK de Mercado Pago')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = 'mp-sdk-v2';
    script.src = 'https://sdk.mercadopago.com/js/v2';
    script.async = true;
    script.onload = () => resolve(window.MercadoPago);
    script.onerror = () => reject(new Error('No se pudo cargar el SDK de Mercado Pago'));
    document.body.appendChild(script);
  });
}

export default function MercadoPagoCheckoutModal({
  title = 'Pagar con Mercado Pago',
  amount,
  publicKey,
  getPreference,
  onClose,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [fallbackUrl, setFallbackUrl] = useState('');
  const [retryTick, setRetryTick] = useState(0);
  const [copied, setCopied] = useState(false);
  const brickController = useRef(null);
  const reactId = useId();
  const containerId = `mp_wallet_container_${reactId.replace(/:/g, '')}`;

  useEffect(() => {
    let active = true;

    const init = async () => {
      try {
        setLoading(true);
        setError('');
        setFallbackUrl('');

        const resolvedPublicKey = publicKey || import.meta.env.VITE_MERCADOPAGO_PUBLIC_KEY;
        if (!resolvedPublicKey) {
          throw new Error('Falta configurar la public key de Mercado Pago en el frontend.');
        }
        if (typeof getPreference !== 'function') {
          throw new Error('No se pudo crear la preferencia de pago.');
        }

        const preference = await getPreference();
        const prefId = preference?.preferenceId || preference?.id || null;
        const initPoint = preference?.initPoint || preference?.sandboxInitPoint || preference?.enlacePago || '';

        if (!active) return;

        if (initPoint) setFallbackUrl(initPoint);

        if (!prefId) {
          if (initPoint) {
            setLoading(false);
            return;
          }
          throw new Error('Mercado Pago no devolvio un identificador de preferencia valido.');
        }

        const MercadoPago = await loadMercadoPagoSdk();
        if (!active) return;

        const mp = new MercadoPago(resolvedPublicKey, { locale: 'es-AR' });
        const bricks = mp.bricks();

        brickController.current = await bricks.create('wallet', containerId, {
          initialization: {
            preferenceId: prefId,
          },
          customization: {
            texts: {
              valueProp: 'security_details',
            },
          },
          callbacks: {
            onReady: () => {
              if (active) setLoading(false);
            },
            onError: (brickError) => {
              if (!active) return;
              setError(brickError?.message || 'Error al inicializar el checkout de Mercado Pago.');
              setLoading(false);
            },
          },
        });
      } catch (e) {
        if (!active) return;
        setError(e?.message || 'No se pudo iniciar el pago.');
        setLoading(false);
      }
    };

    init();

    return () => {
      active = false;
      if (brickController.current && typeof brickController.current.unmount === 'function') {
        brickController.current.unmount();
      }
    };
  }, [containerId, getPreference, publicKey, retryTick]);

  const qrPaymentUrl = fallbackUrl;
  const qrImageUrl = qrPaymentUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=260x260&data=${encodeURIComponent(qrPaymentUrl)}`
    : '';

  const copyLink = async () => {
    if (!fallbackUrl) return;
    try {
      await navigator.clipboard.writeText(fallbackUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      setError('No se pudo copiar el enlace. Copialo manualmente desde el QR.');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{title}</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Cerrar">
            ×
          </button>
        </div>

        <div className="modal-body">
          {amount != null && (
            <div className="payment-amount">
              <span>Monto a pagar</span>
              <strong>
                {Number(amount || 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}
              </strong>
            </div>
          )}

          {loading && <p className="loading-text">Preparando checkout...</p>}

          {error && <div className="alert error">{error}</div>}

          <div id={containerId} className="mp-wallet-container" />

          {qrPaymentUrl && (
            <div className="payment-qr">
              <p>Tambien podes escanear este QR o compartir el enlace:</p>
              <img src={qrImageUrl} alt="QR de pago" />
              <div className="payment-link-actions">
                <button type="button" className="btn-secondary btn-small" onClick={() => void copyLink()} disabled={copied}>
                  {copied ? 'Enlace copiado' : 'Copiar enlace de pago'}
                </button>
                <a href={qrPaymentUrl} target="_blank" rel="noreferrer" className="btn-secondary btn-small">
                  Continuar en Mercado Pago
                </a>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn-ghost" onClick={() => setRetryTick((x) => x + 1)}>
            Reintentar
          </button>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
